/**
 * User options (Options screen), persisted in localStorage['opencraft.options'].
 * DOM-light: only touches localStorage, guarded, so node smoke tests can import it.
 *
 * Mappings (vanilla):
 *  - `sensitivity` is the 0..1 slider; `mouseMultiplier` = 2 * slider (0.5 -> 100%).
 *  - `renderDistance` in chunks (2..16); graphics preset still drives the other effects
 *    (src/settings.ts) but the view radius is overridden by this value.
 *  - `guiScale` 0 = auto, 1..4 explicit.
 *
 * The lead reads the fields and subscribes with `onChange`.
 */

import { PRESETS, type PresetName } from '../settings';

export type GameSettingKey =
  | 'fov' | 'sensitivity' | 'renderDistance' | 'guiScale' | 'graphics' | 'showCoords' | 'viewBobbing' | 'brightness' | 'autoJump';

export interface GameOptions {
  /** Degrees, 30..110 (vanilla slider: 30 "Quake Pro" .. 110, 70 = "Normal"). */
  fov: number;
  /** Slider 0..1; 0.5 = 100% (vanilla). */
  sensitivity: number;
  /** Chunks, 2..16. */
  renderDistance: number;
  /** 0 = Auto, 1..4. */
  guiScale: number;
  graphics: PresetName;
  showCoords: boolean;
  viewBobbing: boolean;
  autoJump: boolean;
  /** Vanilla Brightness (gamma) 0 Moody .. 1 Bright, default 0.5. */
  brightness: number;
}

const KEY = 'opencraft.options';
const GRAPHICS_ORDER: PresetName[] = ['fast', 'fancy', 'ultra'];

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

export class GameSettings implements GameOptions {
  fov = 70;
  sensitivity = 0.5;
  renderDistance = PRESETS.fancy.viewRadius;
  guiScale = 0;
  graphics: PresetName = 'fancy';
  showCoords = false;
  viewBobbing = true;
  autoJump = true;
  brightness = 0.5;

  private listeners: Array<(key: GameSettingKey, s: GameSettings) => void> = [];

  constructor() { this.load(); }

  /** Mouse speed multiplier on the engine's base sensitivity (vanilla: 2 * slider). */
  get mouseMultiplier(): number { return this.sensitivity * 2; }

  /** Subscribe; returns the unsubscribe function. */
  onChange(cb: (key: GameSettingKey, s: GameSettings) => void): () => void {
    this.listeners.push(cb);
    return () => { this.listeners = this.listeners.filter((l) => l !== cb); };
  }

  /** Set one option (clamped/validated), persist, notify. */
  set<K extends GameSettingKey>(key: K, value: GameOptions[K]): void {
    const o = this as GameOptions;
    switch (key) {
      case 'fov': o.fov = Math.round(clamp(value as number, 30, 110)); break;
      case 'sensitivity': o.sensitivity = clamp(value as number, 0, 1); break;
      case 'renderDistance': o.renderDistance = Math.round(clamp(value as number, 2, 16)); break;
      case 'guiScale': o.guiScale = Math.round(clamp(value as number, 0, 4)); break;
      case 'graphics':
        if (!GRAPHICS_ORDER.includes(value as PresetName)) return;
        o.graphics = value as PresetName;
        break;
      case 'showCoords': o.showCoords = !!value; break;
      case 'viewBobbing': o.viewBobbing = !!value; break;
      case 'autoJump': o.autoJump = !!value; break;
      case 'brightness': o.brightness = clamp(value as number, 0, 1); break;
    }
    this.save();
    for (const l of this.listeners) l(key, this);
  }

  load(): void {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return;
      const p = JSON.parse(raw) as Partial<GameOptions>;
      if (typeof p.fov === 'number') this.fov = Math.round(clamp(p.fov, 30, 110));
      if (typeof p.sensitivity === 'number') this.sensitivity = clamp(p.sensitivity, 0, 1);
      if (typeof p.renderDistance === 'number') this.renderDistance = Math.round(clamp(p.renderDistance, 2, 16));
      if (typeof p.guiScale === 'number') this.guiScale = Math.round(clamp(p.guiScale, 0, 4));
      if (p.graphics && GRAPHICS_ORDER.includes(p.graphics)) this.graphics = p.graphics;
      if (typeof p.showCoords === 'boolean') this.showCoords = p.showCoords;
      if (typeof p.viewBobbing === 'boolean') this.viewBobbing = p.viewBobbing;
      if (typeof p.autoJump === 'boolean') this.autoJump = p.autoJump;
      if (typeof p.brightness === 'number') this.brightness = clamp(p.brightness, 0, 1);
    } catch { /* storage unavailable / corrupt — keep defaults */ }
  }

  save(): void {
    try {
      const o: GameOptions = {
        fov: this.fov, sensitivity: this.sensitivity, renderDistance: this.renderDistance,
        guiScale: this.guiScale, graphics: this.graphics, showCoords: this.showCoords,
        viewBobbing: this.viewBobbing, autoJump: this.autoJump, brightness: this.brightness,
      };
      localStorage.setItem(KEY, JSON.stringify(o));
    } catch { /* ignore */ }
  }
}
